import {
	ChangeDetectionStrategy,
	Component,
	inject,
	computed,
} from '@angular/core';
import { UiModule } from 'src/app/ui/ui.module';
import { $intakeActionsViewModel } from './view-models/selectors';
import { select } from '@ngxs/store';
import { ClientIntakeService } from '@services/client-intake/client-intake.service';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { IntakeFormStatus } from '@models/client-intake';
import { KebabOption } from './view-models/models';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './client-intake-card-header.hooks';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@Component({
	selector: 'app-client-intake-card-header',
	templateUrl: './client-intake-card-header.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [UiModule, TestHookDirective],
})
export class ClientIntakeCardHeaderComponent {
	testHooks = TestHooks;
	intakeActionsViewModel_ = select($intakeActionsViewModel);
	navService = inject(NavigationService);
	drawerService = inject(DrawerService);
	clientIntakeService = inject(ClientIntakeService);
	analyticsService = inject(AnalyticsService);

	private defaultKebabItems = [
		{
			id: KebabOption.ViewCustomResponses,
			title: {
				label: 'View custom responses',
				icon: 'north-star',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navService.goToMatter_ClientIntake_Review_CustomQuestionsTab(
					this.intakeActionsViewModel_().matterId,
				);
			},
		},
		{
			id: KebabOption.PreviewForm,
			title: {
				label: 'Preview form',
				icon: 'eye',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navService.goToMatter_ClientIntake_PreviewMode(
					this.intakeActionsViewModel_().matterId,
				);
			},
		},
		{
			id: KebabOption.EditForm,
			title: {
				label: 'Edit form',
				icon: 'edit',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navService.goToMatter_ClientIntake_EditMode(
					this.intakeActionsViewModel_().matterId,
				);
			},
		},
		{
			id: KebabOption.Share,
			title: {
				label: 'Share',
				icon: 'mail',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.shareIntakeForm();
			},
		},
		{
			id: KebabOption.MarkAsComplete,
			title: {
				label: 'Mark as complete',
				icon: 'check',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(IntakeFormStatus.COMPLETE);
			},
		},
		{
			id: KebabOption.Cancel,
			title: {
				label: 'Cancel',
				icon: 'circle-x',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(
					IntakeFormStatus.CANCELLED,
				);
			},
		},
	];

	kebabItems_ = computed(() => {
		const kebabItemsCloned = [...this.defaultKebabItems];
		const intakeFormStatus =
			this.intakeActionsViewModel_().currentIntakeForm?.status;
		const totalResponsesCount =
			this.intakeActionsViewModel_().intakeResponseStatusCounts?.total;
		const missingResponsesCount =
			this.intakeActionsViewModel_().intakeResponseStatusCounts
				?.missingResponses;

		const formHasMissingResponses =
			totalResponsesCount === undefined ||
			totalResponsesCount === null ||
			missingResponsesCount > 0;

		// If form is complete, provide option to set the intake form status back to an earlier state
		if (intakeFormStatus === IntakeFormStatus.COMPLETE) {
			kebabItemsCloned.push({
				id: KebabOption.MarkAsActive,
				title: {
					label: 'Mark as active',
					icon: 'progress-check',
					iconClasses: formHasMissingResponses
						? 'text-blue-600'
						: 'text-amber-600',
				},
				action: () => {
					if (formHasMissingResponses) {
						this.clientIntakeService.setIntakeFormStatus(
							IntakeFormStatus.WAITING_FOR_UPDATES,
						);
					} else {
						this.clientIntakeService.setIntakeFormStatus(
							IntakeFormStatus.REVIEW_UPDATES,
						);
					}
				},
			});
		}

		return kebabItemsCloned;
	});

	hideKebabMenu_ = computed(() => {
		const intakeFormStatus =
			this.intakeActionsViewModel_().currentIntakeForm?.status;
		return (
			!intakeFormStatus ||
			intakeFormStatus === IntakeFormStatus.NOT_INITIATED ||
			intakeFormStatus === IntakeFormStatus.CANCELLED
		);
	});

	sendOrViewIntake() {
		const inProgressStatuses = [
			IntakeFormStatus.WAITING_FOR_UPDATES,
			IntakeFormStatus.REVIEW_UPDATES,
			IntakeFormStatus.COMPLETE,
		];

		const currIntakeFormStatus =
			this.intakeActionsViewModel_().currentIntakeForm?.status;

		// Intake form is in progress, user will be sent to review responses page - logging that analytics event
		if (inProgressStatuses.includes(currIntakeFormStatus)) {
			this.analyticsService.logEvent(
				AnalyticsEvent.INTAKE_PARTIES_VIEW_RESPONSES,
			);
		}

		const matterId = this.intakeActionsViewModel_().matterId;
		this.clientIntakeService.sendOrViewIntake(matterId);
	}
}
